import { atom, getDefaultStore } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'
import * as Sentry from '@sentry/electron/renderer'
import type { AudioNote } from '@highlight/common'
import { GlobalEvents } from '@highlight/ipc'
import { randomId } from '@shared/utils/random'
import { fetchAudioNoteSummary } from '@shared/utils/audio-notes/summaries'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'

const log = GlobalLogger.bind('RendererSummaryGeneration')

/**
 * Renderer-owned summary generation, one entry per note (flagged; see
 * @shared/audio-note-summary-ownership). The backend's summarize route is a
 * start-or-join: calling it for a note already generating replays the latest
 * snapshot, and generation outlives this subscriber, so a dropped stream loses
 * nothing the server won't persist anyway. What's kept here is presentation
 * state; the refetched note is authoritative once `completed` lands.
 */

export type SummaryGenerationStatus = 'preparing' | 'streaming' | 'completed' | 'failed'

export interface SummaryGeneration {
  status: SummaryGenerationStatus
  /** Latest snapshot the backend streamed (full text, not a delta). */
  text: string
  error: string | null
  /** The route refused this account (HTTP 403), so show the upsell rather than a retry. */
  unavailable: boolean
}

/** The non-text slice, so shells can derive a phase without re-rendering per chunk. */
export interface SummaryGenerationView {
  status: SummaryGenerationStatus
  error: string | null
  hasText: boolean
  unavailable: boolean
}

const EMPTY_GENERATION: SummaryGeneration = { status: 'preparing', text: '', error: null, unavailable: false }

export const summaryGenerationsAtom = atom<Record<string, SummaryGeneration>>({})

const sameView = (a: SummaryGenerationView | null, b: SummaryGenerationView | null) =>
  a?.status === b?.status && a?.error === b?.error && a?.hasText === b?.hasText && a?.unavailable === b?.unavailable

export const summaryGenerationViewAtom = atomFamily((noteId: string) =>
  selectAtom(
    summaryGenerationsAtom,
    (all): SummaryGenerationView | null => {
      const generation = all[noteId]
      if (!generation) {
        return null
      }
      return {
        status: generation.status,
        error: generation.error,
        hasText: generation.text.trim().length > 0,
        unavailable: generation.unavailable,
      }
    },
    sameView,
  ),
)

export const summaryGenerationTextAtom = atomFamily((noteId: string) =>
  selectAtom(summaryGenerationsAtom, (all) => all[noteId]?.text ?? ''),
)

// atomFamily caches never evict; these are pure derivations, so recreating an
// evicted entry is safe.
const GENERATION_ATOM_TTL_MS = 60 * 60 * 1000
summaryGenerationViewAtom.setShouldRemove((createdAt) => Date.now() - createdAt > GENERATION_ATOM_TTL_MS)
summaryGenerationTextAtom.setShouldRemove((createdAt) => Date.now() - createdAt > GENERATION_ATOM_TTL_MS)

export function readSummaryGenerationText(noteId: string): string {
  return getDefaultStore().get(summaryGenerationTextAtom(noteId))
}

function patchGeneration(noteId: string, update: Partial<SummaryGeneration>): void {
  getDefaultStore().set(summaryGenerationsAtom, (all) => ({
    ...all,
    [noteId]: { ...(all[noteId] ?? EMPTY_GENERATION), ...update },
  }))
}

/**
 * Snapshots arrive per SSE event, and each one re-renders the markdown behind it. Publishing at most
 * once per interval (matching the main-owned feed) bounds that work; the trailing timer means the
 * last snapshot always lands.
 */
const STREAMING_TEXT_PUBLISH_INTERVAL_MS = 100

const pendingStreamingText = new Map<string, string>()
const streamingTextTimers = new Map<string, ReturnType<typeof setTimeout>>()

function queueStreamingText(noteId: string, text: string): void {
  pendingStreamingText.set(noteId, text)
  if (streamingTextTimers.has(noteId)) {
    return
  }
  streamingTextTimers.set(
    noteId,
    setTimeout(() => flushStreamingText(noteId), STREAMING_TEXT_PUBLISH_INTERVAL_MS),
  )
}

/** Publish the coalesced snapshot now. Call before any change that would otherwise land ahead of it. */
function flushStreamingText(noteId: string): void {
  cancelStreamingTimer(noteId)
  const text = pendingStreamingText.get(noteId)
  if (text === undefined) {
    return
  }
  pendingStreamingText.delete(noteId)
  patchGeneration(noteId, { status: 'streaming', text })
}

/** Drop the coalesced snapshot, for a state that supersedes it. */
function discardStreamingText(noteId: string): void {
  cancelStreamingTimer(noteId)
  pendingStreamingText.delete(noteId)
}

function cancelStreamingTimer(noteId: string): void {
  const timer = streamingTextTimers.get(noteId)
  if (timer !== undefined) {
    clearTimeout(timer)
    streamingTextTimers.delete(noteId)
  }
}

const inFlight = new Map<string, Promise<void>>()

export interface StartSummaryGenerationOptions {
  /**
   * Omit to start, join, or retry the note's current generation. Pass a fresh
   * UUID to regenerate a summary the backend already completed; without one
   * the route replays the finished result.
   */
  generationId?: string
  trigger?: 'auto-save' | 'meeting-view'
}

/**
 * Start (or join) summary generation for one note. Never rejects: failures land
 * in the note's generation state. Concurrent calls for the same note share one
 * request.
 */
export function startSummaryGeneration(noteId: string, options: StartSummaryGenerationOptions = {}): Promise<void> {
  const existing = inFlight.get(noteId)
  if (existing) {
    log.info(`Joining in-flight summary generation for audio note ${noteId} (trigger=${options.trigger ?? 'unknown'})`)
    return existing
  }
  const mode = options.generationId ? 'regenerate' : 'start-or-join'
  log.info(
    `Starting renderer summary generation for audio note ${noteId} (mode=${mode}, trigger=${options.trigger ?? 'unknown'}, generationId=${options.generationId ?? 'default'})`,
  )
  const run = runSummaryGeneration(noteId, options.generationId).finally(() => {
    inFlight.delete(noteId)
    discardStreamingText(noteId)
    log.info(`Renderer summary generation settled for audio note ${noteId}`)
  })
  inFlight.set(noteId, run)
  return run
}

export function regenerateSummary(noteId: string): Promise<void> {
  return startSummaryGeneration(noteId, { generationId: randomId(), trigger: 'meeting-view' })
}

async function runSummaryGeneration(noteId: string, generationId: string | undefined): Promise<void> {
  discardStreamingText(noteId)
  patchGeneration(noteId, { ...EMPTY_GENERATION })
  log.info(`Summary state changed for audio note ${noteId} (status=preparing)`)

  if (__IS_BROWSER__) {
    log.info(`Using browser summary fallback for audio note ${noteId}`)
    await runBrowserSummaryGeneration(noteId)
    return
  }

  let result: { title: string; summary: string } | null
  let receivedText = false
  let reportedFailure = false
  try {
    const accessToken = await AuthIpcRenderer.getAccessToken()
    log.info(`Opening summary stream for audio note ${noteId} (generationId=${generationId ?? 'default'})`)
    result = await fetchAudioNoteSummary(
      noteId,
      accessToken,
      {
        onStreamEvent: (text) => {
          if (text.trim().length > 0) {
            if (receivedText) {
              queueStreamingText(noteId, text)
              return
            }
            // The phase change from preparing shows immediately; only the chunks after it are coalesced.
            receivedText = true
            patchGeneration(noteId, { status: 'streaming', text })
            log.info(`Summary state changed for audio note ${noteId} (status=streaming, chars=${text.length})`)
          }
        },
        onError: (error, httpStatus) => {
          reportedFailure = true
          flushStreamingText(noteId)
          log.error(
            `Summary state changed for audio note ${noteId} (status=failed, httpStatus=${httpStatus ?? 'none'}, partialChars=${readSummaryGenerationText(noteId).length}): ${error}`,
          )
          patchGeneration(noteId, { status: 'failed', error, unavailable: httpStatus === 403 })
        },
        onSuccess: () => {},
      },
      generationId,
    )
  } catch (error) {
    // fetchAudioNoteSummary reports its own failures through onError; this is the token fetch.
    Sentry.captureException(error)
    flushStreamingText(noteId)
    log.error(
      `Failed to prepare renderer summary generation for audio note ${noteId}: ${error instanceof Error ? (error.stack ?? error.message) : String(error)}`,
    )
    patchGeneration(noteId, { status: 'failed', error: 'Error generating summary.' })
    return
  }

  if (!result) {
    flushStreamingText(noteId)
    if (!reportedFailure) {
      log.warn(
        `Summary stream ended without a result for audio note ${noteId} (partialChars=${readSummaryGenerationText(noteId).length})`,
      )
    }
    return
  }

  discardStreamingText(noteId)
  patchGeneration(noteId, { status: 'completed', text: result.summary })
  log.info(
    `Summary state changed for audio note ${noteId} (status=completed, summaryChars=${result.summary.length}, titleChars=${result.title.length})`,
  )
  // Every window's react-query provider invalidates the audio-note caches on
  // this, including the one emitting it.
  GlobalEvents.emit('audio-note:summary-ready')
}

/** The pure-web build has no backend: settle with whatever summary the fake store holds. */
async function runBrowserSummaryGeneration(noteId: string): Promise<void> {
  try {
    const note = (await AudioNoteIpcRenderer.getAudioNote(noteId)) as AudioNote | undefined
    const summary = note?.summary?.trim() ? note.summary : 'Summary generated in browser mode.'
    await AudioNoteIpcRenderer.updateAudioNote({ id: noteId, updates: { summary } })
    patchGeneration(noteId, { status: 'completed', text: summary })
    log.info(`Browser summary fallback completed for audio note ${noteId} (summaryChars=${summary.length})`)
    GlobalEvents.emit('audio-note:summary-ready')
  } catch (error) {
    log.error(
      `Browser summary fallback failed for audio note ${noteId}: ${error instanceof Error ? (error.stack ?? error.message) : String(error)}`,
    )
    patchGeneration(noteId, { status: 'failed', error: 'Error generating summary.' })
  }
}
